import { Edit, GripVertical, Users } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ShopItem } from '../../service/api';
import Coin from '../Coin';
import type { DragHandleProps } from '../SortableList';

interface ManagerShopItemCardProps {
  item: ShopItem;
  /** Optional drag handle props — when provided, renders a grip icon for reordering */
  dragHandleProps?: DragHandleProps;
}

export default function ManagerShopItemCard({
  item,
  dragHandleProps,
}: ManagerShopItemCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isCollectable = item.purchaseType === 'collectable';

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/manager/shop-item/${item._id}`);
  };

  return (
    <Card className="mb-3 shadow-sm border">
      <Card.Body>
        <Row className="align-items-center">
          {dragHandleProps && (
            <Col
              xs="auto"
              className="d-flex align-items-center pe-0"
              style={dragHandleProps.style}
              {...dragHandleProps.listeners}
              {...dragHandleProps.attributes}
            >
              <GripVertical size={20} className="text-muted" />
            </Col>
          )}
          <Col xs="auto">
            <DynamicIcon name={item.icon} size={32} className="text-primary" />
          </Col>
          <Col>
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0">{item.title}</h6>
              {isCollectable && (
                <Badge bg="info" className="d-flex align-items-center gap-1">
                  <Users size={12} />
                  {t('manager.shop.purchaseTypeCollectable')}
                </Badge>
              )}
            </div>
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-2">
            <span className="fw-bold">{item.price}</span>
            <Coin size="xs" />
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleEdit}
              title={t('common.edit')}
            >
              <Edit size={16} />
            </Button>
          </Col>
        </Row>
        <Row className="align-items-center pt-2">
          <Col>
            {item.description && (
              <p className="text-muted small mb-0">{item.description}</p>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
